import type { StatusTone } from "./status";
import { money } from "./tariff";
import type { PublicOrder, PublicOrderState } from "./types";

/**
 * A public charge order, translated for an operator. pay-ui spec §9.5.
 *
 * Pure and apart from the components, in the same spirit as `sessions.ts`: the
 * decisions worth checking are which of the twelve states is alarming, what an
 * order's money adds up to, and how old the oldest debt in the refund queue is.
 * None of that needs a DOM.
 *
 * ## Why this file does not use `format.ts`'s `kwh`
 *
 * That formatter renders one decimal, which is right for the sessions table —
 * enough to tell two sessions apart, narrow enough to scan down a column. It is
 * wrong here. §11.3 settles a public order by multiplying delivered energy by a
 * per-kWh price, so the delivered figure is the *basis of a charge* rather than an
 * operational statistic, and 0.1 kWh of rounding is 4.5¢ at the tariff this fleet
 * sells at. An operator reconciling a driver's complaint against the gateway needs
 * the number the arithmetic actually used.
 */

/**
 * Watt-hours as kWh at full precision, which is Wh — the unit the meter reports
 * and the unit the money was computed from.
 */
const ENERGY = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** What a figure renders as when there is not one. Matches `tariff.ts`. */
const NOT_A_FIGURE = "—";

/**
 * Exported so every energy figure on a public order agrees. The delivered kWh in
 * the table, the same figure in the detail masthead and the session's committed
 * energy are all the basis of the same charge, and rendering one of them at a
 * coarser precision than the others is how two screens disagree about one number.
 */
export function energyKwh(wh: string | null): string {
  if (wh === null) return NOT_A_FIGURE;

  const value = Number(wh);
  if (wh.trim() === "" || !Number.isFinite(value)) return NOT_A_FIGURE;

  return ENERGY.format(value / 1000);
}

/**
 * How alarming each state is, at a glance down the table.
 *
 * The one that matters is `faulted`: money taken and not yet returned is the only
 * thing on this page that should shout, and §9.5 makes that the normal ending
 * rather than the exceptional one.
 *
 * `expired_unplugged` and `start_failed` are faulted for a subtler reason.
 * `refundOrderInFull` passes through them into `refund_required` inside one call,
 * so an order found *resting* in either is one whose second transition never
 * happened: the money is settled, the energy is zero, and nothing is working on the
 * debt. Those are the rows worth interrupting an operator for.
 */
const STATE_TONES: Readonly<Record<PublicOrderState, StatusTone>> = {
  pending: "transitional",
  awaiting_plug: "transitional",
  starting: "transitional",
  charging: "charging",
  stopping: "transitional",
  completed: "available",
  /** Nobody paid and nothing was taken. Not a fault, and not worth a colour. */
  expired: "idle",
  expired_unplugged: "faulted",
  start_failed: "faulted",
  refund_required: "faulted",
  refunded: "available",
  refund_failed: "faulted",
  /**
   * The hold tail. `capture_required` is transitional rather than faulted — the
   * sweeper is working on it and the money is still on the card — but
   * `capture_failed` is the loudest row on the page: the energy was delivered, the
   * hold is lapsing, and every minute it sits there is revenue about to stop
   * existing. That is the opposite direction of harm from `refund_required`, and
   * it earns the same colour.
   */
  capture_required: "transitional",
  captured: "available",
  /** Nothing was owed and nothing was taken. A clean ending, not a fault. */
  released: "idle",
  capture_failed: "faulted",
};

/**
 * How each state reads in the table's filter, in the order an operator scans them.
 *
 * Here rather than in the page because a `Record` keyed on the union stops
 * compiling when §8 grows a state, and a filter list that silently omits one is a
 * search that returns nothing for orders that exist.
 */
export const ORDER_STATE_FILTERS: Readonly<Record<PublicOrderState, string>> = {
  pending: "Pending payment",
  awaiting_plug: "Awaiting plug-in",
  starting: "Starting",
  charging: "Charging",
  stopping: "Stopping",
  completed: "Completed",
  expired: "Expired unpaid",
  expired_unplugged: "Paid, never plugged in",
  start_failed: "Start failed",
  refund_required: "Refund required",
  refunded: "Refunded",
  refund_failed: "Refund failed",
  /**
   * Worded as money, not as mechanism. An operator filtering this table is asking
   * "who is owed something" or "what did we not collect", and "Capture required"
   * answers neither unless they already know the rail's vocabulary.
   */
  capture_required: "Capture pending",
  captured: "Captured from hold",
  released: "Hold released, nothing owed",
  capture_failed: "Capture failed — not collected",
};

/**
 * `string` rather than `PublicOrderState`, because a server deploy can add a state
 * this bundle has never heard of and a table row must not crash over it. The
 * `Record` above still fails to compile when the union grows here.
 */
export function orderStateTone(state: string): StatusTone {
  return STATE_TONES[state as PublicOrderState] ?? "idle";
}

export interface OrderMoney {
  /**
   * Money that actually left the driver's account, or an em dash when none has.
   *
   * Not "the order's amount". Under `pre_auth` that figure is an authorisation the
   * driver's bank is holding and the merchant has not received, and it stays that
   * way until the capture — so on a held order this reads as an em dash right up to
   * `captured`, and then as what the capture collected.
   */
  readonly taken: string;
  /**
   * What `taken` should be captioned. It travels with the figure rather than
   * living in the column header because on a mixed deployment both flows appear in
   * one table, and a header cannot be right for both rows.
   */
  readonly takenLabel: string;
  /**
   * The authorisation still sitting on the card, or `undefined` under `purchase`
   * where there is no such thing — the money left the account at settlement.
   */
  readonly held: string | undefined;
  /** What the close decided was left over. */
  readonly unused: string;
  /** What is still owed back. Zero on an order that owes nothing. */
  readonly owed: string;
}

/**
 * The money on an order, as sentences.
 *
 * **Nothing here keys on `amountMinor` alone.** Under `purchase` an order that was
 * never settled has taken nothing, and rendering the amount it *would* have taken
 * puts a charge on the screen beside a driver who was never charged — the same
 * mistake §9.4 warns about on the driver's side of the glass.
 *
 * Under `pre_auth` the trap is sharper, because `settled_at` is set for a hold
 * exactly as it is for a purchase: the payment step completed either way. Keying
 * "Paid" on it labelled the full authorisation of every held order as money the
 * driver had handed over, when the merchant had received none of it and might never
 * receive most of it. An operator reading that column was being told the wrong
 * thing about somebody else's money.
 *
 * `unused` and `owed` are kept apart on purpose: §11.3's floor leaves a `completed`
 * order holding unused money that is deliberately not being returned, so the two
 * disagree on exactly the rows where the difference is the point. Under `pre_auth`
 * `owed` is always zero — the server derives it that way — because unused money on
 * a hold was never taken and so cannot be owed back.
 */
export function orderMoney(order: PublicOrder): OrderMoney {
  const unused =
    order.unusedMinor === null ? NOT_A_FIGURE : money(order.unusedMinor, order.currency);
  const owed = money(order.refundOwedMinor, order.currency);

  if (order.paymentFlow === "pre_auth") {
    return {
      taken: capturedFigure(order),
      takenLabel: "Taken from hold",
      held: money(order.amountMinor, order.currency),
      unused,
      owed,
    };
  }

  return {
    taken: order.settledAt === null ? NOT_A_FIGURE : money(order.amountMinor, order.currency),
    takenLabel: "Paid",
    held: undefined,
    unused,
    owed,
  };
}

/**
 * What a capture actually collected: the authorised amount less the part the close
 * decided was not used.
 *
 * Only in `captured`. Every earlier state is money still sitting on the card, and
 * `released` and `capture_failed` are endings where nothing was collected at all —
 * a `$0.00` there would read as a charge of zero rather than as no charge.
 *
 * A `captured` order with no committed unused figure gets an em dash rather than
 * the full authorisation. That combination is a data fault, and falling back to the
 * authorised amount would answer it by overstating revenue — the wrong direction to
 * be wrong in when the figure is what an operator reconciles against the gateway.
 */
function capturedFigure(order: PublicOrder): string {
  if (order.state !== "captured" || order.unusedMinor === null) return NOT_A_FIGURE;

  return money(String(BigInt(order.amountMinor) - BigInt(order.unusedMinor)), order.currency);
}

/**
 * The two refund reasons that mean **no energy was delivered**.
 *
 * They take precedence over the delivered figure because that figure is `0` on
 * these orders — a true number that reads as "nothing happened" when what happened
 * is that somebody paid and got nothing back for it.
 *
 * `unused_balance` and `operator` are absent deliberately: both sit on orders that
 * did deliver energy, and the kWh figure is the more useful sentence there.
 */
/**
 * Named because `orderOutcome` reaches it twice: once as a refund reason and once
 * as a state, on the same order a few milliseconds apart. Two copies of one
 * sentence is how the two readings come to disagree.
 */
const STATION_NEVER_STARTED = "The station never started the charge";

/** What happened, without the money's fate — `refundEnding` supplies that. */
const REFUND_REASONS: Readonly<Record<string, string>> = {
  never_charged: "Paid but never charged",
  start_failed: STATION_NEVER_STARTED,
};

/**
 * The reason outlives the debt — it stays on the order after the refund lands —
 * so the sentence's ending comes from the state, not the reason.
 */
function refundEnding(order: PublicOrder): string {
  return order.state === "refunded" ? " — refunded in full" : " — a full refund is owed";
}

/**
 * One sentence saying what became of this order.
 *
 * A table of twelve states and four refund reasons is not readable as raw
 * vocabulary — `refund_required / never_charged` is two enum values an operator has
 * to translate in their head, on the row where translating it wrongly costs someone
 * their money. The state pill stays for scanning; this is the row's meaning.
 */
export function orderOutcome(order: PublicOrder): string {
  const reason = order.refundReason === null ? undefined : REFUND_REASONS[order.refundReason];
  if (reason !== undefined) return `${reason}${refundEnding(order)}`;

  switch (order.state) {
    case "pending":
      return "Waiting for payment";
    case "awaiting_plug":
      return "Paid, waiting for the driver to plug in";
    case "starting":
      return "Paid, waiting for the station to start";
    case "charging":
      return "Charging now";
    case "stopping":
      return "Stopping";
    case "expired":
      return "The hold lapsed before payment";
    /**
     * §12.3's two ways to be paid and never charge, caught before the refund was
     * queued. Both carry `delivered_wh = 0` and no refund reason yet, so the
     * default below would render them as "0.000 kWh delivered" — a true number
     * that reads as a charge which happened and gave nothing.
     */
    case "expired_unplugged":
      return "Paid but never plugged in — a full refund is owed";
    case "start_failed":
      return `${STATION_NEVER_STARTED}${refundEnding(order)}`;
    /**
     * The hold tail reads in the driver's terms, because that is who an operator is
     * about to answer a question from. "Released" in particular has to say *not
     * charged* out loud: it is the one ending where a driver saw an amount
     * disappear from their available balance and will ring up convinced they paid.
     */
    case "capture_required":
      return "Charged energy is being taken from the hold";
    case "captured":
      return order.deliveredWh === null
        ? "Taken from the hold"
        : `${energyKwh(order.deliveredWh)} kWh delivered, taken from the hold`;
    case "released":
      return "Nothing was charged — the hold was released in full";
    case "capture_failed":
      return "Energy was delivered and the hold was never collected — revenue at risk";
    default:
      return order.deliveredWh === null
        ? "No energy recorded"
        : `${energyKwh(order.deliveredWh)} kWh delivered`;
  }
}

/**
 * How long the oldest outstanding debt has been waiting.
 *
 * §9.5: "Alert on the queue's age, not its size. A queue of 40 refunds cleared
 * daily is healthy; one refund sitting for a week is somebody's money."
 *
 * Compact rather than `format.ts`'s "3h ago", because this is a duration in a
 * metric slot and not a timestamp in a table — "ago" reads as a clock reading where
 * what is meant is a debt's age. Floored rather than rounded: "at least this old"
 * is the honest reading of a debt, and rounding 3h50m up to 4h overstates it.
 *
 * `now` is a parameter so the figure is testable without faking the clock.
 */
export function queueAge(oldestOwedAt: string | null, now: number = Date.now()): string {
  if (oldestOwedAt === null) return NOT_A_FIGURE;

  const since = Date.parse(oldestOwedAt);
  if (Number.isNaN(since)) return NOT_A_FIGURE;

  const minutes = Math.floor((now - since) / 60_000);
  // A debt incurred in the future is a clock problem, not a negative age. There is
  // nothing honest to print for it, and "-3h" would read as a rendering fault.
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  return `${Math.floor(hours / 24)}d`;
}

/**
 * How a single payment attempt reads. The five values
 * `payment_transaction_status_check` permits, and no others.
 *
 * `refunded` is `available` rather than something of its own: on this page a
 * completed refund is the *good* ending, and §11.3 makes it the common one.
 */
const PAYMENT_TONES: Readonly<Record<string, StatusTone>> = {
  initiated: "transitional",
  approved: "available",
  declined: "faulted",
  refunded: "available",
  refund_failed: "faulted",
};

export function paymentTone(status: string): StatusTone {
  return PAYMENT_TONES[status] ?? "idle";
}

/**
 * The refund queue's total debt, in a currency it is safe to name.
 *
 * `RefundQueueSummaryDto` carries **no currency**: the server sums `owedMinor`
 * across every owed row in one statement. That is correct on a single-currency
 * deployment and would silently add riel to cents on a mixed one — and §11.1 is
 * emphatic that a figure rendered against the wrong minor-unit exponent is the
 * worst mistake available in this system.
 *
 * So the symbol is attached only when the loaded rows agree on one currency. When
 * they do not, the bare minor-unit integer is printed with a caption saying so:
 * odd-looking on purpose, because a number that looks wrong gets questioned and a
 * number wearing the wrong currency symbol does not.
 *
 * The rows are a *sample* — the summary spans the whole queue and they span one
 * page — so this can only ever refuse to name a currency, never name the wrong one
 * confidently. That asymmetry is the point.
 */
export function queueTotal(
  summary: { readonly owedMinor: string } | undefined,
  rows: readonly PublicOrder[],
): string {
  if (summary === undefined) return NOT_A_FIGURE;

  const currency = soleCurrency(rows);

  return currency === undefined ? summary.owedMinor : money(summary.owedMinor, currency);
}

/** The caption under `queueTotal`, which has to say when the figure is unitless. */
export function queueTotalHint(rows: readonly PublicOrder[]): string {
  return soleCurrency(rows) === undefined
    ? "minor units — no single currency on this page"
    : "still to be returned";
}

/** The one currency every row shares, or `undefined` if they disagree or there are none. */
export function soleCurrency(rows: readonly PublicOrder[]): string | undefined {
  const first = rows[0]?.currency;
  if (first === undefined) return undefined;

  return rows.every((row) => row.currency === first) ? first : undefined;
}
